import { useGetCountriesQuery } from 'api/countriesApi'
import Cookies from 'js-cookie'
import { LANGUAGE_COOKIE_NAME } from 'localization'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { DEFAULT_CURRENCY_CODE, STORAGE } from 'shared/enums/SitePreferences'
import useCurrency from 'shared/hooks/useCurrency'
import { Country } from 'shared/types/Country'
import { Currency } from 'shared/types/Currency'
import { ShoppingRegionData } from 'shared/types/RegionPreference'

type ShoppingRegionContextProviderProps = {
  availableCurrencies: Currency[]
  children: ReactNode
  clientSideLocale: string
  shoppingRegionData: ShoppingRegionData
}

type ShoppingRegionState = {
  availableCountries: Country[]
  availableCurrencies: Currency[]
  country: string
  countryName: string
  currencyCode: string
  locale: string
  setCountryContext: Dispatch<SetStateAction<string>>
  setCountryNameContext: Dispatch<SetStateAction<string>>
  setCurrencyCodeContext: Dispatch<SetStateAction<string>>
  setLocaleContext: Dispatch<SetStateAction<string>>
}

const ShoppingRegionContext = createContext<ShoppingRegionState>({
  availableCountries: [],
  availableCurrencies: [],
  country: '',
  countryName: '',
  currencyCode: '',
  locale: '',
  setCountryContext: () => {},
  setCountryNameContext: () => {},
  setCurrencyCodeContext: () => {},
  setLocaleContext: () => null,
})

export const useShoppingRegionContext = () => useContext(ShoppingRegionContext)

const ShoppingRegionContextProvider = ({
  availableCurrencies,
  children,
  shoppingRegionData,
}: ShoppingRegionContextProviderProps) => {
  const { data: countriesData, isLoading: isLoadingGetCountries } = useGetCountriesQuery()
  const availableCountries = isLoadingGetCountries
    ? []
    : countriesData?.countries.filter((country) => country.shipsTo) || []

  const [locale, setLocaleContext] = useState(Cookies.get(LANGUAGE_COOKIE_NAME) || '')
  const [country, setCountryContext] = useState(
    Cookies.get(STORAGE.COUNTRY) || shoppingRegionData?.country,
  )
  const [countryName, setCountryNameContext] = useState(
    Cookies.get(STORAGE.COUNTRYNAME) || shoppingRegionData?.countryName,
  )
  const [currencyCode, setCurrencyCodeContext] = useState(
    Cookies.get(STORAGE.CURRENCYCODE) || shoppingRegionData?.currencyCode,
  )

  // set currency in backend
  const { setCurrency } = useCurrency()
  useEffect(() => {
    setCurrency(currencyCode)
  }, [])

  useEffect(() => {
    Cookies.set(STORAGE.CURRENCY, currencyCode || DEFAULT_CURRENCY_CODE, { expires: 365 })
  }, [currencyCode])

  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 1)

  const shoppingRegionValues: ShoppingRegionState = {
    availableCountries,
    availableCurrencies,
    country,
    countryName,
    currencyCode,
    locale,
    setCountryContext,
    setCountryNameContext,
    setCurrencyCodeContext,
    setLocaleContext,
  }
  return (
    <ShoppingRegionContext.Provider value={shoppingRegionValues}>
      {children}
    </ShoppingRegionContext.Provider>
  )
}

export default ShoppingRegionContextProvider
